import { moment } from '@hotjar/webapp/lib/moment';
import { capitalize } from '@hotjar/insights-common/lib/string';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { ResponseLimitInfo } from '@hotjar/feedback/domain/common/Responses/ResponseLimitInfo';
import { DataLimitsCard, WidgetVariant } from '../DataLimitsCard';

import { AskLimitsCardStyles as S } from './AskLimitsCard.styles';
import { CardWidget } from './components/CardWidget/CardWidget';

interface AskLimitsCardProps {
  currentTier: string;
  rolloverDate: Date | null;
  responseLimitInfo: ResponseLimitInfo | undefined;
  lastActiveDaysAgo: number;
  onVerificationClick: () => void;
  isError: boolean;
}

const getWidgetVariant = (
  responseLimitInfo: ResponseLimitInfo | undefined,
  lastActiveDaysAgo: number,
  isError: boolean,
) => {
  if (isError) {
    return WidgetVariant.INFO;
  }

  if (lastActiveDaysAgo > 0) {
    return WidgetVariant.WARNING;
  }

  const lowResponsesRemaining =
    responseLimitInfo?.unlimited === false
      ? responseLimitInfo.remaining / responseLimitInfo.limit < 0.5
      : false;

  if (lowResponsesRemaining) {
    return WidgetVariant.WARNING;
  }

  return WidgetVariant.SUCCESS;
};

export const AskLimitsCard = ({
  currentTier,
  rolloverDate,
  responseLimitInfo,
  lastActiveDaysAgo,
  onVerificationClick,
  isError,
}: AskLimitsCardProps): JSX.Element => {
  const { t } = useTranslationWithPrefix('billing:product-select-upsell-modal.ask-limits-card');
  const planMonthlyResponses =
    responseLimitInfo?.unlimited === false
      ? responseLimitInfo.limit
      : t('responses-remaining.unlimited');
  const rolloverDateLabel = rolloverDate ? moment(rolloverDate).format('DD/MM/YYYY') : '-';

  return (
    <DataLimitsCard
      infoList={[
        {
          label: t('current-plan.label'),
          value: t('current-plan.value', {
            currentPlan: capitalize(currentTier),
            planMonthlyResponses: isError ? '-' : planMonthlyResponses,
          }),
        },
        {
          label: t('responses-refresh.label'),
          value: rolloverDateLabel,
        },
      ]}
      widgetVariant={getWidgetVariant(responseLimitInfo, lastActiveDaysAgo, isError)}
    >
      <S.CardBody spacing="x1">
        <CardWidget
          responseLimitInfo={responseLimitInfo}
          lastActiveDaysAgo={lastActiveDaysAgo}
          onVerificationClick={onVerificationClick}
          isError={isError}
        />
      </S.CardBody>
    </DataLimitsCard>
  );
};
